<template>
  <div>
    <!-- Card moderno para a tabela -->
    <div class="bg-white rounded-xl shadow-lg overflow-hidden">
      <!-- Header do card -->
      <div class="border-b border-gray-200 bg-gray-50 px-6 py-4">
        <div class="flex items-center gap-2">
          <i class="fas fa-user-check text-gray-600 text-xl"></i>
          <div>
            <h2 class="text-xl font-bold text-gray-900">Lista de Aprovação</h2>
            <p class="text-sm text-gray-500 mt-1">Usuários aguardando aprovação</p>
          </div>
        </div>
      </div>

      <LoadingComponent v-if="this.loading" />

      <!-- Tabela Desktop -->
      <div class="hidden sm:block overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(user, index) in users" :key="index" class="hover:bg-gray-50 transition-colors duration-150">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div class="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                      <i class="fas fa-user-clock text-yellow-600"></i>
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">
                      {{ user.name }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-600">{{ user.email }}</div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                <div class="flex items-center justify-end gap-2">
                  <button 
                    @click="createNewUser(user, index)"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-150">
                    <i class="fas fa-check mr-1"></i>
                    Aceitar
                  </button>
                  <ModalUserAssign :options=userOptions :userApproval=user @update="removeRow" :rowId=index @error="showError"/>
                  <button 
                    @click="rejectUser(user, index)"
                    class="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-150">
                    <i class="fas fa-times mr-1"></i>
                    Rejeitar
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Cards Mobile -->
      <div class="block sm:hidden p-4 space-y-4">
        <div v-for="(user, index) in users" :key="user._id" class="bg-white border-2 border-yellow-200 rounded-xl p-4 hover:border-yellow-300 transition-colors duration-150">
          <div class="flex items-start gap-3 mb-3">
            <div class="flex-shrink-0">
              <div class="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <i class="fas fa-user-clock text-yellow-600 text-lg"></i>
              </div>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-base font-semibold text-gray-900 truncate">{{ user.name }}</p>
              <p class="text-sm text-gray-600 truncate">{{ user.email }}</p>
            </div>
          </div>
          
          <!-- Botões de ação mobile -->
          <div class="grid grid-cols-2 gap-2">
            <button 
              @click="createNewUser(user, index)"
              class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-white bg-green-600 hover:bg-green-700 transition-colors duration-150">
              <i class="fas fa-check"></i>
              <span>Aceitar</span>
            </button>
            <button 
              @click="rejectUser(user, index)"
              class="flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium rounded-lg text-white bg-red-600 hover:bg-red-700 transition-colors duration-150">
              <i class="fas fa-times"></i>
              <span>Rejeitar</span>
            </button>
            <div class="col-span-2">
              <ModalUserAssign :options=userOptions :userApproval=user :tableStyle=false @update="removeRow" :rowId=index @error="showError"/>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import UserService from '@/services/userService';
import LoadingComponent from '@/components/LoadingComponent.vue';
import ModalUserAssign from '@/components/ModalUserAssign'
import { useToast } from "vue-toastification";

export default {
  name: 'ApprovalTable',
  setup() {
      const toast = useToast();
      return { toast }
  },
  components: {
    ModalUserAssign,
    LoadingComponent
  },
  mounted() {
    UserService.getApprovalUsers().then(data => {
      this.users = data
      this.loading = false;
    }).catch(err => {
      console.log(err);
      this.loading = false;
      this.showError("Não foi possível carregar as aprovações pendentes.");
    });

    UserService.getUsers().then(users => {
      let options = users.map(data => {
        return {
          value: data._id,
          label: data.name
        }
      });
      this.userOptions = options
    }).catch(err => {
      console.log(err);
      this.showError("Não foi possível carregar a lista de usuários.");
    })
  },
  methods: {
    removeRow(index, message) {
      if (message !== undefined) {
        this.toast.success(message);
      }
      this.users.splice(index, 1);
    },
    createNewUser(user, index) {
      user.approvalId = user._id
      UserService.assignUser(user).then(data => {
        this.toast.success(data.message);
        this.removeRow(index)
      }).catch(err => {
        this.showError(err.message)
      });
    },

    rejectUser(user, index) {
      UserService.deleteApprovalUser(user._id).then(() => {
        this.toast.success("Removido com sucesso");
        this.removeRow(index)
      }).catch(err => {
        this.showError(err.message)
      });
    },
    showError(message) {
      this.toast.error(message)
    }
  },
  data(){
    return {
      users: [],
      userOptions: [],
      loading: true
    }
  },
}
</script>